import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher';
import { useTeam } from '@/contexts/TeamContext';
import {
  FileText,
  ListChecks,
  ChatCircleDots,
  House,
  SignOut,
  User,
  Lightning,
  Briefcase,
  Tray,
  Scales,
  List,
  X,
  Gear,
  Pulse,
  Clock
} from '@phosphor-icons/react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: House, testId: 'nav-dashboard' },
  { path: '/inbox', label: 'Inbox', icon: Tray, testId: 'nav-inbox' },
  { path: '/tasks', label: 'Tasks', icon: ListChecks, testId: 'nav-tasks' },
  { path: '/matters', label: 'Matters', icon: Briefcase, testId: 'nav-matters' },
  { path: '/legal-calendar', label: 'Legal Calendar', icon: Scales, testId: 'nav-legal-calendar' },
  { path: '/ai-assistant', label: 'AI', icon: ChatCircleDots, testId: 'nav-ai' },
];

const TEAM_NAV_ITEMS = [
  { path: '/team/deadlines', label: 'Deadlines', icon: Clock, testId: 'nav-deadlines' },
  { path: '/team/activity', label: 'Activity', icon: Pulse, testId: 'nav-activity' },
];

function Header({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { activeTeam } = useTeam() || {};

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  const isActive = (path) => location.pathname === path || (path === '/matters' && location.pathname.startsWith('/matters'));

  const allNavItems = activeTeam ? [...NAV_ITEMS, ...TEAM_NAV_ITEMS] : NAV_ITEMS;

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200 pt-[env(safe-area-inset-top)]" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2.5 group" data-testid="logo-link">
            <img
              src="/helmcore-logo.png"
              alt="Helm Core"
              className="h-8 md:h-9 w-auto object-contain"
            />
          </button>
          <WorkspaceSwitcher />

          <nav className="hidden md:flex items-center gap-1">
            {allNavItems.map(({ path, label, icon: Icon, testId }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`relative px-3.5 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all duration-200 ${
                  isActive(path)
                    ? 'text-navy bg-blue-50'
                    : 'text-slate-500 hover:text-navy hover:bg-slate-50'
                }`}
                data-testid={testId}
              >
                <Icon size={18} weight={isActive(path) ? 'duotone' : 'regular'} />
                <span>{label}</span>
                {isActive(path) && (
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-navy rounded-full" />
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-slate-500 hover:text-slate-700 p-1.5"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2.5 hover:bg-slate-50 transition-colors px-2.5 py-1.5 rounded-lg"
              data-testid="user-menu-btn"
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full border border-slate-200"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                  <User size={16} className="text-navy" />
                </div>
              )}
              <span className="hidden md:block text-sm font-medium text-slate-700">{user?.name}</span>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 shadow-lg rounded-lg overflow-hidden">
                {activeTeam && (
                  <button
                    onClick={() => { navigate('/team/settings'); setShowUserMenu(false); }}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-2 text-slate-700 border-b border-slate-100"
                    data-testid="team-settings-btn"
                  >
                    <Gear size={18} weight="regular" />
                    <span className="text-sm font-medium">Team Settings</span>
                  </button>
                )}
                <button
                  onClick={() => { navigate('/settings'); setShowUserMenu(false); }}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-2 text-slate-700 border-b border-slate-100"
                  data-testid="settings-btn"
                >
                  <Gear size={18} weight="regular" />
                  <span className="text-sm font-medium">Settings</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-2 text-slate-700"
                  data-testid="logout-btn"
                >
                  <SignOut size={18} weight="regular" />
                  <span className="text-sm font-medium">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
          {allNavItems.map(({ path, label, icon: Icon, testId }) => (
            <button
              key={path}
              onClick={() => { navigate(path); setMobileOpen(false); }}
              className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2.5 text-sm font-medium transition-colors ${
                isActive(path)
                  ? 'text-navy bg-blue-50'
                  : 'text-slate-500 hover:text-navy hover:bg-slate-50'
              }`}
              data-testid={`mobile-${testId}`}
            >
              <Icon size={18} weight={isActive(path) ? 'duotone' : 'regular'} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </header>
  );
}

export default Header;
